import "../css/app.css";

import "iceberg-components/dist/index.css";

import * as Sentry from "@sentry/vue";
import {
    DoughnutHoverShadow,
    DoughnutLabel,
    HtmlLegendPlugin,
} from "@/Support/chartJsPlugins.js";
import { globalLoading } from "@/Support/helpers.js";
import {
    type IconDefinition,
    library,
} from "@fortawesome/fontawesome-svg-core";

/* import specific icons */
import {
    faArrowLeftToLine,
    faArrowRight,
    faArrowRightFromBracket,
    faArrowRightToLine,
    faArrowUpArrowDown,
    faArrowUpFromBracket,
    faAward,
    faBan,
    faBarsStaggered,
    faBell,
    faBold,
    faBoxArchive,
    faBuildingMagnifyingGlass,
    faBullseyeArrow,
    faCalendarCheck,
    faCalendarClock,
    faCalendarWeek,
    faCalendarXmark,
    faChargingStation,
    faChartLine,
    faChartMixed,
    faCheck,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
    faChfSign,
    faCircleCheck,
    faCircleInfo,
    faCircleXmark,
    faClipboardCheck,
    faClockRotateLeft,
    faCopy,
    faDollarSign,
    faDownload,
    faEnvelopeOpen,
    faEuroSign,
    faExpand,
    faEye,
    faEyeSlash,
    faFamilyPants,
    faFileCertificate,
    faFileCheck,
    faFileExport,
    faFileLock,
    faFilter,
    faFilterCircleXmark,
    faFolderArrowUp,
    faGear,
    faHandHoldingHeart,
    faHandHoldingSeedling,
    faHandshake,
    faHandshakeSlash,
    faHouse,
    faItalic,
    faLightbulbOn,
    faList,
    faListOl,
    faListTree,
    faLocationDot,
    faLockAlt,
    faMagnifyingGlass,
    faMedal,
    faMemoCircleInfo,
    faMinus,
    faMoneyCheckPen,
    faPaperclip,
    faPaperclipVertical,
    faPencil,
    faPercent,
    faPlus,
    faRoute,
    faSave,
    faSortAlt,
    faSpinnerThird,
    faSterlingSign,
    faTag,
    faThumbTack,
    faTrash,
    faTrashAlt,
    faTrashCan,
    faUnderline,
    faUser,
    faUserCheck,
    faUserHelmetSafety,
    faUserPen,
    faUserXmark,
    faUsers,
    faXmark,
} from "@fortawesome/pro-light-svg-icons";
import {
    faTriangleExclamation,
    faMinus as fasMinus,
    faPlus as fasPlus,
} from "@fortawesome/pro-solid-svg-icons";

/* import font awesome icon component */
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { createInertiaApp, router } from "@inertiajs/vue3";
import { coloris, init } from "@melloware/coloris";
import "@melloware/coloris/dist/coloris.css";
import "@vue-flow/core/dist/style.css";
import axios from "axios";
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart,
    Chart as ChartJS,
    Tooltip as ChartToolTip,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Title,
} from "chart.js";
import {
    Accordion,
    ActivityMenu,
    Alert,
    Attachment,
    Batches,
    Breadcrumb,
    Button,
    ButtonBadges,
    Checkbox,
    ConversationSelectList,
    CreateConversation,
    DashboardTile,
    DashboardTileHeader,
    DashboardTileSubtitleValue,
    DashboardTileToggleHeader,
    DashboardTileTotalComparison,
    DashboardTileUnitValue,
    DashboardTileValue,
    Dropdown,
    ErrorMessage,
    IconButton,
    InputDatePicker,
    InputFields,
    InputTimePicker,
    Labels,
    LaravelPaginationButtons,
    LaravelPaginationNumber,
    LibraryTileDetails,
    Links,
    ListDetails,
    ListView,
    MessageList,
    Messaging,
    MetricTiles,
    Modal,
    MultistepIcon,
    NumberInput,
    NumberSelector,
    PaginationButtons,
    PaginationNumber,
    ProgressBar,
    RadioButtons,
    ResponderTiles,
    Slider,
    StandardModal,
    SubAccordions,
    Tabs,
    TileDetails,
    TileIcon,
    Toggle,
    Tooltip,
    TreeView,
    Typography,
    Wizzywyg,
} from "iceberg-components";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { createPinia } from "pinia";
import VCalendar from "v-calendar";
import { type DefineComponent, createApp, h } from "vue";

import { ZiggyVue } from "../../vendor/tightenco/ziggy";

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

init();
coloris({
    el: "#coloris",
    theme: "large",
    format: "hex",
    defaultColor: "#FFFFFF",
    swatches: [
        "#FD3C42",
        "#F9A963",
        "#F6F36D",
        "#63FD75",
        "#5A9CFB",
        "#C55AF5",
        "#868585",
    ],
});

const icons = [
    faCalendarXmark,
    faHouse,
    faBarsStaggered,
    faGear,
    faBell,
    faChevronDown,
    faChevronUp,
    faChevronRight,
    faChevronLeft,
    faTrash,
    faListTree,
    faHandHoldingHeart,
    faHandHoldingSeedling,
    faMoneyCheckPen,
    faThumbTack,
    faArrowLeftToLine,
    faChartMixed,
    faArrowRightToLine,
    faClipboardCheck,
    faCalendarWeek,
    faBullseyeArrow,
    faTrashAlt,
    faSave,
    faPencil,
    faXmark,
    faLockAlt,
    faPlus,
    faMinus,
    faExpand,
    faFilter,
    faFilterCircleXmark,
    faSortAlt,
    faFileExport,
    faArrowUpFromBracket,
    faArrowRightFromBracket,
    faFolderArrowUp,
    faCircleInfo,
    faSterlingSign,
    faUserPen,
    faArrowRight,
    faBoxArchive,
    faChartLine,
    faRoute,
    faChargingStation,
    faCheck,
    faDollarSign,
    faEuroSign,
    faBold,
    faUnderline,
    faItalic,
    faList,
    faListOl,
    faPaperclipVertical,
    faCircleXmark,
    faDownload,
    faPaperclip,
    faHandshake,
    faHandshakeSlash,
    faEnvelopeOpen,
    faFileLock,
    faChfSign,
    faFamilyPants,
    faUserCheck,
    faUser,
    faUserXmark,
    faLocationDot,
    faAward,
    faUsers,
    faUserHelmetSafety,
    faCalendarCheck,
    faBuildingMagnifyingGlass,
    faMagnifyingGlass,
    faCalendarClock,
    faEyeSlash,
    faEye,
    faTrashCan,
    faBan,
    faClockRotateLeft,
    faTag,
    faCopy,
    faFileCertificate,
    faMedal,
    faPercent,
    faLightbulbOn,
    faArrowUpArrowDown,
    faFileCheck,
    faMemoCircleInfo,
    faSpinnerThird,
    faCircleCheck,
    faTriangleExclamation,
    fasPlus,
    fasMinus,
] satisfies IconDefinition[];
/* add icons to the library  */
library.add(...icons);

axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

const appName = import.meta.env.VITE_APP_NAME || "Loop";

const pinia = createPinia();

createInertiaApp({
    title: () => `${appName}`,
    resolve: (name) =>
        resolvePageComponent(
            `./Pages/${name}.vue`,
            import.meta.glob<DefineComponent>("./Pages/**/*.vue"),
        ),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) })
            .component("font-awesome-icon", FontAwesomeIcon)
            .use(plugin)
            .use(ZiggyVue)
            .use(VCalendar, {})
            .use(pinia)
            .component("ProgressBar", ProgressBar)
            .component("Slider", Slider)
            .component("GroupButton", Button)
            .component("Typography", Typography)
            .component("InputFields", InputFields)
            .component("IconButton", IconButton)
            .component("Alert", Alert)
            .component("Batches", Batches)
            .component("ButtonBadges", ButtonBadges)
            .component("ActivityMenu", ActivityMenu)
            .component("Labels", Labels)
            .component("Links", Links)
            .component("Checkbox", Checkbox)
            .component("MultistepIcon", MultistepIcon)
            .component("NumberInput", NumberInput)
            .component("PaginationButtons", PaginationButtons)
            .component("PaginationNumber", PaginationNumber)
            .component("LaravelPaginationButtons", LaravelPaginationButtons)
            .component("LaravelPaginationNumber", LaravelPaginationNumber)
            .component("RadioButton", RadioButtons)
            .component("Toggle", Toggle)
            .component("Tooltip", Tooltip)
            .component("Modal", Modal)
            .component("NumberSelector", NumberSelector)
            .component("Accordion", Accordion)
            .component("SubAccordions", SubAccordions)
            .component("Breadcrumb", Breadcrumb)
            .component("Dropdown", Dropdown)
            .component("MetricTile", MetricTiles)
            .component("TileIcon", TileIcon)
            .component("TileDetails", TileDetails)
            .component("InputDatePicker", InputDatePicker)
            .component("StandardModal", StandardModal)
            .component("LibraryTileDetails", LibraryTileDetails)
            .component("Attachment", Attachment)
            .component("InputTimePicker", InputTimePicker)
            .component("ListView", ListView)
            .component("ListDetails", ListDetails)
            .component("Wizzywyg", Wizzywyg)
            .component("Messaging", Messaging)
            .component("ConversationSelectList", ConversationSelectList)
            .component("MessageList", MessageList)
            .component("CreateConversation", CreateConversation)
            .component("Tabs", Tabs)
            .component("TreeView", TreeView)
            .component("ResponderTiles", ResponderTiles)
            .component("ErrorMessage", ErrorMessage)
            .component("DashboardTile", DashboardTile)
            .component("DashboardTileHeader", DashboardTileHeader)
            .component("DashboardTileSubtitleValue", DashboardTileSubtitleValue)
            .component("DashboardTileToggleHeader", DashboardTileToggleHeader)
            .component(
                "DashboardTileTotalComparison",
                DashboardTileTotalComparison,
            )
            .component("DashboardTileUnitValue", DashboardTileUnitValue)
            .component("DashboardTileValue", DashboardTileValue);

        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration(),
            ],
            // Performance Monitoring
            tracesSampleRate: import.meta.env.VITE_SENTRY_DSN_PUBLIC, //  Capture 100% of the transactions
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [
                "localhost",
                import.meta.env.VITE_BASE_URL,
                import.meta.env.APP_URL,
            ],
            // Session Replay
            replaysSessionSampleRate: import.meta.env
                .VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            replaysOnErrorSampleRate: import.meta.env
                .VITE_SENTRY_REPLAYS_ON_ERRORS_SESSION_SAMPLE_RATE, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        });

        app.mount(el);
    },
    progress: {
        delay: 0,
        color: "#4B5563",
        includeCSS: true,
    },
});
window.addEventListener("popstate", () => {
    // popstate event listener to also apply loading spinner when
    // using browser navigation
    globalLoading(true);
});

router.on("finish", () => {
    globalLoading(false);
});

router.on("before", () => {
    globalLoading(true);
});

// register chartJs plugins
ChartJS.register(
    ArcElement,
    ChartToolTip,
    Legend,
    DoughnutHoverShadow,
    DoughnutLabel,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    LineElement,
    PointElement,
    HtmlLegendPlugin,
);

Chart.defaults.borderColor = "#9cb3ca";
Chart.defaults.scale.grid.color = "#eaeff6";
Chart.defaults.color = "#00182f";
Chart.defaults.font.family = "Pagabo Sans Display";
Chart.defaults.font.weight = 300;
Chart.defaults.plugins.tooltip = {
    ...Chart.defaults.plugins.tooltip,
    backgroundColor: "#A8E6FF",
    titleColor: "#001932",
    bodyColor: "#001932",
    titleAlign: "center",
    bodyAlign: "center",
    footerAlign: "center",
    displayColors: false,
    titleFont: () => ({ family: "Pagabo Sans Display", size: 14, weight: 500 }),
    bodyFont: () => ({ family: "Pagabo Sans Display", size: 14, weight: 200 }),
}; // Override history.replaceState to log all calls
